import * as R from 'ramda';
import React from 'react';
import {withRouter} from 'react-router-dom';
import {compose} from 'recompose';

import {translate} from 'iss/strings';
import {eventStates, getEvent, startEvent, getPreviousAnswers, submitAnswers, submitCorrectionalRemarks} from 'iss/service/EvaluationEventService';
import {getForm} from 'iss/service/FormService';
import styles from 'iss/styles/FillEvaluationEditor.styl';
import dates from 'iss/utils/dates';
import subscribeTo from 'iss/utils/subscriptionUtils';
import {withLocalStorage} from 'iss/utils/withLocalStorage';
import {Button, CancelButton, SubmitButton} from 'iss/widgets/Buttons';
import {translateEventAction} from 'iss/widgets/domainTranslations';
import {FlexRow} from 'iss/widgets/Flex';
import {Form, FormSection, Input} from 'iss/widgets/Forms';
import {ErrorBox, RestResultMessage} from 'iss/widgets/MessageBox';
import {AnswerHeadingSelector, AnswerRadioSelector} from 'iss/widgets/Selectors';

const FillEvaluationEditor = compose(
  withRouter,
  subscribeTo(({eventId}) => getEvent(eventId), "event"),
  subscribeTo(({eventId}) => getPreviousAnswers(eventId), "previousAnswers"),
  subscribeTo(({formId}) => getForm(formId), "form"),
  withLocalStorage(
    ({eventId, previousAnswers})=> "eventAnswerStorage-1.1-"+eventId+"-"+previousAnswers.batchNumber,
    "answers", "setAnswers", "clearStoredAnswers",
    ({previousAnswers, form}) => {
      return {
        heading: form.headings[0],
        eventInfo: R.pick(
          ["additionalEventInfo", "eventInfoClarification", "contactPerson"],
          previousAnswers
        ),
        questions: R.fromPairs(previousAnswers.answers.map(answer => [answer.questionId, answer]))
      };
    }
  )
)(class FillEvaluationEditor extends React.Component {
  state = {
    backendError: null,
    showEventInfo: true,
  };
  getNextHeading = (heading) => {
    const {headings} = this.props.form;
    return headings[headings.indexOf(heading) + 1];
  };
  isLastHeading = (heading) => {
    return heading.sortOrder === R.last(this.props.form.headings).sortOrder;
  };

  handleKeyDown = (e) => {
    const {key} = e;
    if (key == "Enter") {
      e.preventDefault();
    }
  };

  goToFirstHeading = (formApi, form) => {
    this.setState({showEventInfo: false});
    formApi.setValue("heading", form.headings[0]);
  };

  startEvent = (event) => {
    if (event.state == eventStates.readyForEvaluation) {
      startEvent(event.id)
        .subscribe(
          () => {},
          () => {}
        );
    }
  };

  saveAnswers = ({questions, eventInfo}) => {
    const getAnswerContents = (answer, key) => {
      return {
        questionId: key,
        gradeId: answer.gradeId,
        remarks1: answer.remarks1 || '',
        remarks2: answer.remarks2 || '',
        imageIds: null // Despite this answering from web does not seem to lose any uploaded images. Nice, but why?
      };
    };
    const hasAnswer = (answer, key) => R.any(prev => prev.questionId == key, this.props.previousAnswers.answers)
                                      || !R.isNil(answer.gradeId)
                                      || !R.isNil(answer.remarks1)
                                      || !R.isNil(answer.remarks2)
                                      || answer.imageIds !== null;
    const resultAnswers = R.values(R.mapObjIndexed(getAnswerContents, R.pickBy(hasAnswer, questions)));

    const data = {
      additionalEventInfo: eventInfo.additionalEventInfo || '',
      contactPerson: eventInfo.contactPerson || '',
      eventInfoClarification: eventInfo.eventInfoClarification || '',
      answers: resultAnswers,
      batchNumber: 0
    };

    const saveAction = this.props.event.state === eventStates.readyForCorrectionalRemarks ?
                        submitCorrectionalRemarks
                        : submitAnswers;
    saveAction(this.props.event.id, data).subscribe(
      () => {
        this.props.clearStoredAnswers();
        this.props.history.push("/event/"+this.props.event.id);
      },
      (errorResponse) => this.setState({backendError: errorResponse})
    );
  };

  render() {
    const {form, event, answers, setAnswers} = this.props;
    return <Form values={answers} valueUpdater={setAnswers} onSubmit={this.saveAnswers} onKeyDown={this.handleKeyDown}>
        {(values, formApi) =>
          <React.Fragment>
            <h1>{form.name}</h1>
            {this.state.showEventInfo ?
              <GeneralEventInfo event={event} alternativeInfoFieldNames={form.alternativeInfoFieldNames} aimedGradeText={form.aimedGrade ? form.aimedGrade.text : '-'} />
              :
              <React.Fragment>
                <label>{translate('fillEvaluationEditor.chooseHeading')}</label>
                <FlexRow flexAllChildren>
                  <AnswerHeadingSelector flex="1 0 50%" inline id={"headings"} headings={form.headings} valuePath="heading" />
                  <Button flex="none" id="show-general-info"
                    onClick={() => this.setState({showEventInfo: true})}
                  >
                    {translate('fillEvaluationEditor.showGeneralInfo')}
                  </Button>
                </FlexRow>
                <Questions
                  heading={values.heading}
                  gradingScale={form.gradingScale}
                  remarksFieldNames={form.remarksFieldNames}
                  key={values.heading.id}
                  canEditAnswer={event.state === eventStates.readyForEvaluation || event.state === eventStates.started}
                />
                <Button id="nextHeading"
                  disabled={this.isLastHeading(values.heading)}
                  onClick={() => {
                      formApi.setValue("heading", this.getNextHeading(values.heading));
                      scroll(0,0);
                    }
                  }>
                    {translate('fillEvaluationEditor.nextHeading')}
                </Button>
              </React.Fragment>
            }
            <RestResultMessage id="backendError" result={this.state.backendError} />
            <hr />
            <div className="form-group">

              { this.state.showEventInfo &&
                <React.Fragment>
                  { event.state === eventStates.readyForEvaluation &&
                    <Button id="start-evaluation" buttonLook="primary" onClick={() => {
                      this.startEvent(this.props.event);
                      this.goToFirstHeading(formApi, form);
                    }}>
                      {translateEventAction('startEvaluation')}
                    </Button>
                  }
                  { event.state === eventStates.started &&
                    <Button id="fill-answers" buttonLook="primary" onClick={() => this.goToFirstHeading(formApi, form)}>
                      {translateEventAction('fillEvaluation')}
                    </Button>
                  }
                  { event.state === eventStates.readyForCorrectionalRemarks &&
                    <Button id="fill-correctional-remarks" buttonLook="primary" onClick={() => this.goToFirstHeading(formApi, form)}>
                      {translateEventAction('fillCorrectionalRemarks')}
                    </Button>
                  }
                </React.Fragment>
              }

              { !this.state.showEventInfo &&
                <SubmitButton id="submit-answers">
                  {translate('common.save')}
                </SubmitButton>
              }

              <CancelButton link={"/event/"+this.props.event.id} />
            </div>
          </React.Fragment>
        }
    </Form>;
  }
});

function GeneralEventInfo({event, aimedGradeText, alternativeInfoFieldNames}) {
  const [additionalInfoLabel, clarificationLabel] = alternativeInfoFieldNames;
    return <React.Fragment>
      <ErrorBox id="stateError" message={
          (event.state !== eventStates.readyForCorrectionalRemarks) ?
          translate('fillEvaluationEditor.stateError')
          : null
        } />
      <strong>{translate('fillEvaluationEditor.address')}</strong>
      <p id="event-info-street-address">{event.site.streetAddress}</p>
      <strong>{translate('fillEvaluationEditor.plannedDate')}</strong>
      <p id="event-info-planned-date">{event.plannedDate}</p>
      <strong>{translate('fillEvaluationEditor.currentDate')}</strong>
      <p id="event-info-current-date">{dates.formatUTC(new Date())}</p>
      <strong>{translate('fillEvaluationEditor.aimedGrade')}</strong>
      <p id="event-info-aimed-grade">{aimedGradeText}</p>

    <Input label="fillEvaluationEditor.contactPerson" id="event-info-contact-person" valuePath="eventInfo.contactPerson" maxLength="255" showCounter={true}/>
    <Input label={clarificationLabel || translate('fillEvaluationEditor.eventInfoClarification')}  noTranslateLabel={true} id="event-info-clarification" valuePath="eventInfo.eventInfoClarification" maxLength="500" showCounter={true}/>
    <Input label={additionalInfoLabel || translate('fillEvaluationEditor.additionalEventInfo')}  noTranslateLabel={true} id="event-info-additional" valuePath="eventInfo.additionalEventInfo" maxLength="500" showCounter={true}/>
    </React.Fragment>;
}

function Questions({heading, gradingScale, remarksFieldNames, canEditAnswer}) {
  return <React.Fragment>
    <div id="questions">
      <h2>{heading.title}</h2>
      {heading.questions.map((question, questionNr) =>
        <Question id={"question"+question.id} question={question} gradingScale={gradingScale} key={questionNr} remarksFieldNames={remarksFieldNames} canEditAnswer={canEditAnswer} />)
      }
    </div>
  </React.Fragment>;
}

function Question({question, gradingScale, remarksFieldNames, canEditAnswer}) {
  const [remarks1Label, remarks2Label] = remarksFieldNames;

  return <FormSection valuePath={["questions", question.id]}>
    <h3 id={"question"+question.id} data-question-id={question.id}>{question.name}</h3>
    <p className="text-muted">{question.description}</p>
    <AnswerRadioSelector id={"answer-"+question.id} className={styles.answerOptions} valuePath="gradeId" grades={gradingScale.grades} disabled={!canEditAnswer} />
    <Input id={"remarks1-"+question.id} valuePath="remarks1"
      label={remarks1Label || translate('fillEvaluationEditor.remarks1')} noTranslateLabel={true}
      maxLength="2000" showCounter={true}  disabled={!canEditAnswer}/>
    <Input id={"remarks2-"+question.id} valuePath="remarks2"
      label={remarks2Label || translate('fillEvaluationEditor.remarks2')} noTranslateLabel={true}
      maxLength="2000" showCounter={true}/>
  </FormSection>;
}

export default FillEvaluationEditor;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/FillEvaluationEditor.jsx